// Admin API client. No token handling needed here — Cloudflare Access
// (configured on the /admin* route in the Zero Trust dashboard, see
// docs/admin-guide.md) injects the Cf-Access-Jwt-Assertion header
// automatically once the operator has authenticated; the backend
// independently re-verifies it (functions/lib/auth.ts).

export class AdminAuthenticationError extends Error {
  constructor(message = "Your admin session has expired. Reloading the secure sign-in…") {
    super(message);
    this.name = "AdminAuthenticationError";
  }
}

export async function parseAdminResponse<T>(res: Response): Promise<T> {
  const contentType = res.headers.get("content-type")?.toLowerCase() ?? "";
  const redirectedToAccess = res.redirected && (() => {
    try {
      return new URL(res.url).hostname.endsWith(".cloudflareaccess.com");
    } catch {
      return false;
    }
  })();

  if (redirectedToAccess) throw new AdminAuthenticationError();

  if (!contentType.includes("application/json")) {
    throw new Error(
      res.status === 429
        ? "The admin service is temporarily rate limited by Cloudflare. Please try again after the daily limit resets."
        : "The admin service is temporarily unavailable because Cloudflare returned the app page instead of API data. Signing in again will not fix this; please try again shortly.",
    );
  }

  const body = await res.json().catch(() => null) as Record<string, unknown> | null;
  if (!res.ok) {
    const error = body?.error as { message?: string } | undefined;
    throw new Error(error?.message ?? "Admin request failed.");
  }
  if (!body || typeof body !== "object") {
    throw new Error("The admin API returned an empty response. Please reload and try again.");
  }
  return body as T;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api/admin${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
  try {
    return await parseAdminResponse<T>(res);
  } catch (error) {
    if (error instanceof AdminAuthenticationError && typeof window !== "undefined") {
      // The HTML page may still be in the browser cache even though the
      // Access session protecting its API has expired. Reloading the same
      // protected admin URL lets Cloudflare Access perform its normal
      // sign-in redirect and then return the operator to this screen.
      window.location.reload();
    }
    throw error;
  }
}

export interface CreateRecipientLocationBody {
  postalMode: string;
  establishmentName?: string;
  receivingMail?: boolean;
  freeMailEligible?: boolean;
  validFrom?: string | null;
  validUntil?: string | null;
  notes?: string;
  activate?: boolean;
  address?: {
    addressLine1: string;
    addressLine2?: string;
    addressLine3?: string;
    addressLine4: string;
    addressLine5?: string;
    addressLine6?: string;
    townCity: string;
    county?: string;
    postcode: string;
    bfpoNumber?: string;
    country: string;
    routingReference?: string;
    routingReferenceVerified: boolean;
    sourceDocument?: string;
    notes?: string;
  };
}

export const adminApi = {
  listRecipientLocations: () => request<{ locations: RecipientLocationRow[] }>("/recipient-locations"),
  createRecipientLocation: (body: CreateRecipientLocationBody) =>
    request<{ location: RecipientLocationRow; route: PostalRouteRow | null }>("/recipient-locations", { method: "POST", body: JSON.stringify(body) }),
  patchRecipientLocation: (id: string, body: Record<string, unknown>) =>
    request<{ location: RecipientLocationRow }>(`/recipient-locations/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  listPostalRoutes: (locationId?: string) => request<{ routes: PostalRouteRow[] }>(`/postal-routes${locationId ? `?locationId=${locationId}` : ""}`),
  createPostalRoute: (body: Record<string, unknown>) => request<{ route: PostalRouteRow }>("/postal-routes", { method: "POST", body: JSON.stringify(body) }),
  patchPostalRoute: (id: string, body: Record<string, unknown>) =>
    request<{ route: PostalRouteRow }>(`/postal-routes/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  listRules: (params: Record<string, string> = {}) =>
    request<{ rules: RuleRow[]; dueForReviewCount: number }>(`/rules?${new URLSearchParams(params).toString()}`),
  createRule: (body: Record<string, unknown>) => request<{ rule: RuleRow }>("/rules", { method: "POST", body: JSON.stringify(body) }),
  patchRule: (id: string, body: Record<string, unknown>) => request<{ rule: RuleRow }>(`/rules/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  duplicateRule: (id: string) => request<{ rule: RuleRow }>(`/rules/${id}/duplicate`, { method: "POST" }),

  listRuleSources: () => request<{ sources: RuleSourceRow[] }>("/rule-sources"),
  createRuleSource: (body: Record<string, unknown>) => request<{ source: RuleSourceRow }>("/rule-sources", { method: "POST", body: JSON.stringify(body) }),
  markSourceChecked: (id: string) => request<{ source: RuleSourceRow }>("/rule-sources", { method: "POST", body: JSON.stringify({ markCheckedId: id }) }),

  getIntegrationHealth: () => request<{ providers: IntegrationHealthEntry[] }>("/integration-health"),
  getAiHealth: () => request<{ ok: boolean; detail: string }>("/ai-health"),
  getSettings: () => request<{ tilePickerEnabled: boolean }>("/settings"),
  updateSettings: (body: { tilePickerEnabled: boolean }) => request<{ tilePickerEnabled: boolean }>("/settings", { method: "PATCH", body: JSON.stringify(body) }),
  getAuditLog: () => request<{ entries: AuditLogEntry[] }>("/audit-log"),
  getShipmentIntents: () => request<{ intents: ShipmentIntentSummary[]; failedCount: number; needsReviewCount: number }>("/shipment-intents"),
  getShipmentIntentDetail: (id: string) => request<{ intent: Record<string, unknown> }>(`/shipment-intents/${id}`),
  retryFulfilment: (id: string) => request<{ shipment: unknown }>(`/shipment-intents/${id}/retry-fulfilment`, { method: "POST" }),
  refundShipment: (id: string, reason: string) =>
    request<{ shipment: unknown }>(`/shipment-intents/${id}/refund`, { method: "POST", body: JSON.stringify({ reason }) }),

  listProviderServices: (providerId?: string) =>
    request<{ services: ProviderServiceRow[] }>(`/provider-services${providerId ? `?providerId=${providerId}` : ""}`),
  createRateBand: (body: { providerId: string; carrierCode: string; rateBand: RateBandCore & Record<string, unknown> }) =>
    request<{ service: ProviderServiceRow }>("/provider-services", { method: "POST", body: JSON.stringify(body) }),
  toggleRateBand: (id: string, active: boolean) =>
    request<{ service: ProviderServiceRow }>(`/provider-services/${id}`, { method: "PATCH", body: JSON.stringify({ active }) }),

  createParcel2GoSandboxTest: (select?: { carrier: string; service: string }) =>
    request<{ shipmentIntentId: string; quote: SandboxQuote; checkout?: SandboxCheckout; status: string }>("/parcel2go-sandbox-test", {
      method: "POST",
      body: JSON.stringify(select ?? {}),
    }),
  getParcel2GoQuotesPreview: () => request<{ quotes: SandboxQuote[] }>("/parcel2go-quotes-preview"),
  getParcel2GoLiveQuotesPreview: () => request<{ ok: boolean; detail: string; raw?: unknown }>("/parcel2go-live-quotes-preview"),
  checkProviderPayment: (id: string) => request<{ intent: SandboxIntentRow }>(`/shipment-intents/${id}/check-payment`, { method: "POST" }),
  getRecoveryCandidates: (id: string) => request<{ candidates: RecoveryCandidate[] }>(`/shipment-intents/${id}/recovery-candidates`),
  linkRecoveredOrder: (id: string, candidate: RecoveryCandidate) =>
    request<{ intent: SandboxIntentRow }>(`/shipment-intents/${id}/link-recovered-order`, { method: "POST", body: JSON.stringify(candidate) }),
  clearUncertain: (id: string) => request<{ intent: SandboxIntentRow }>(`/shipment-intents/${id}/clear-uncertain`, { method: "POST" }),
};

export interface SandboxQuote {
  carrier: string;
  service: string;
  carrierDisplayName: string;
  serviceDisplayName: string;
  priceGbp: number;
  estimatedDeliveryDays: string;
  requiresPrinter: boolean;
  printerlessAvailable: boolean;
  isMock: boolean;
}

export interface SandboxCheckout {
  mode: string;
  redirectUrl: string;
  providerShipmentId?: string;
}

export interface RecoveryCandidate {
  orderId: string;
  hash: string;
  orderLineId: string;
  paidDate: string | null;
  postcode: string;
  serviceSlug: string;
  valueGbp: number;
}

export interface SandboxIntentRow {
  id: string;
  status: string;
  checkout_json: string | null;
  label_json: string | null;
  tracking_json: string | null;
  error_json: string | null;
  order_creation_uncertain: number;
  needs_manual_review: number;
  payment_captured_gbp: number | null;
}

/** Fields every provider's static rate band shares; each provider may add its own carrier-service-code field on top — see src/admin/IntegrationHealth.tsx. */
export interface RateBandCore {
  service: string;
  serviceDisplayName: string;
  maxWeightGrams: number;
  priceGbp: number;
  estimatedDeliveryDays: string;
  trackedLevel: "NONE" | "BASIC" | "FULL";
  compensationCoverGbp: number;
}

export interface ProviderServiceRow {
  id: string;
  provider_id: string;
  carrier_code: string;
  service_code: string;
  display_name: string;
  active: number;
  metadata_json: string;
}

export interface RecipientLocationRow {
  id: string;
  recipient_id: string;
  postal_mode: string;
  establishment_name: string | null;
  receiving_mail: number;
  free_mail_eligible: number;
  status: string;
  valid_from: string | null;
  valid_until: string | null;
  last_verified_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface PostalRouteRow {
  id: string;
  recipient_location_id: string;
  address_line1: string | null;
  address_line2: string | null;
  address_line3: string | null;
  address_line4: string | null;
  address_line5: string | null;
  address_line6: string | null;
  town_city: string | null;
  county: string | null;
  postcode: string | null;
  bfpo_number: string | null;
  country: string;
  routing_reference: string | null;
  routing_reference_verified: number;
  source_document: string | null;
  status: string;
  last_verified_at: string | null;
  notes: string | null;
}

export interface RuleRow {
  id: string;
  name: string;
  category: string;
  description: string | null;
  status: string;
  carrier: string | null;
  service: string | null;
  postal_mode: string | null;
  effect: string;
  conditions_json: string;
  user_message: string | null;
  admin_notes: string | null;
  last_verified_at: string | null;
  hard_block: number;
  active: number;
  source_name: string | null;
  source_url: string | null;
}

export interface RuleSourceRow {
  id: string;
  name: string;
  url: string;
  organisation: string | null;
  checked_at: string | null;
}

export interface IntegrationHealthEntry {
  id: string;
  displayName: string;
  kind: string;
  dbStatus: string;
  capabilities: Record<string, boolean>;
  notes: string | null;
  health: { ok: boolean; detail?: string };
}

export interface AuditLogEntry {
  id: string;
  actor: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  created_at: string;
}

export interface ShipmentIntentSummary {
  id: string;
  status: string;
  selected_provider_id: string | null;
  error_json: string | null;
  fulfilment_attempts: number;
  needs_manual_review: number;
  price_mismatch: number;
  refund_status: string | null;
  created_at: string;
  updated_at: string;
}
